import { FEEDBACK_LOG_LINE_MAX, FEEDBACK_LOG_LINES } from "@charter/shared";

/**
 * A small in-memory log of what happened on this device, for feedback notes (docs/feedback.md):
 * connection changes, server rejections, warnings and errors, app backgrounding. The widget
 * attaches the last minutes of it to every note so whoever fixes the note (a person or the
 * feedback bot's builder) sees what the tester's client saw. Nothing here leaves the device on
 * its own; it is only read when a note is sent.
 */
interface Line {
  at: number;
  text: string;
}

const KEEP = 400;
const lines: Line[] = [];

/** Record one line (cheap; call it from anywhere, any time). */
export function clientLog(text: string): void {
  // cut before anything else: a huge warning object must not cost memory or time per frame
  const cut =
    text.length > FEEDBACK_LOG_LINE_MAX * 2 ? text.slice(0, FEEDBACK_LOG_LINE_MAX * 2) : text;
  lines.push({ at: Date.now(), text: cut.replace(/\s+/g, " ").trim() });
  if (lines.length > KEEP) lines.splice(0, lines.length - KEEP);
}

const stamp = (ms: number) => new Date(ms).toISOString().slice(11, 23);

/**
 * The lines from the last `windowMs` (default 15 minutes), oldest first, "HH:MM:SS.mmm text"
 * (UTC), capped to what a note may carry (the newest lines win).
 */
export function recentLog(windowMs = 15 * 60_000, now = Date.now()): string[] {
  return lines
    .filter((l) => l.at >= now - windowMs)
    .slice(-FEEDBACK_LOG_LINES)
    .map((l) => `${stamp(l.at)} ${l.text}`.slice(0, FEEDBACK_LOG_LINE_MAX));
}

/** @internal tests */
export function clearClientLog(): void {
  lines.length = 0;
}

const describe = (v: unknown): string => {
  if (v instanceof Error) return `${v.name}: ${v.message}`;
  if (typeof v === "string") return v;
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
};

let installed = false;

/**
 * Feed the log from the page: console warnings and errors (still printed as before), uncaught
 * errors, the network going away and coming back, and the app being hidden or shown.
 */
export function installClientLog(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;
  const tap =
    (level: string, orig: (...args: unknown[]) => void) =>
    (...args: unknown[]) => {
      clientLog(`console.${level} ${args.map(describe).join(" ")}`);
      orig(...args);
    };
  console.warn = tap("warn", console.warn.bind(console));
  console.error = tap("error", console.error.bind(console));
  window.addEventListener("error", (e) => clientLog(`uncaught ${e.message}`));
  window.addEventListener("unhandledrejection", (e) =>
    clientLog(`unhandled rejection ${describe(e.reason)}`),
  );
  window.addEventListener("online", () => clientLog("network online"));
  window.addEventListener("offline", () => clientLog("network offline"));
  document.addEventListener("visibilitychange", () => clientLog(`app ${document.visibilityState}`));
  clientLog(
    `page loaded ${location.pathname} · ${navigator.userAgent} · ${window.innerWidth}x${window.innerHeight}@${window.devicePixelRatio}`,
  );
}
